'use client';

import { useEffect, useRef } from 'react';
import type { GoonDeckPostEffectsConfig } from '@/lib/goondeck-foil-presets';
import { maskShapeStyle } from '@/lib/goondeck-foil-render';

const CANVAS_W = 300;
const CANVAS_H = 450;

function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const value = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function drawCoveredImage(ctx: CanvasRenderingContext2D, image: HTMLImageElement, w: number, h: number) {
  const scale = Math.max(w / image.naturalWidth, h / image.naturalHeight);
  const drawW = image.naturalWidth * scale;
  const drawH = image.naturalHeight * scale;
  ctx.drawImage(image, (w - drawW) / 2, (h - drawH) / 2, drawW, drawH);
}

function smoothstep(edge0: number, edge1: number, value: number) {
  const t = Math.max(0, Math.min(1, (value - edge0) / Math.max(0.0001, edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

function drawEmboss(ctx: CanvasRenderingContext2D, image: HTMLImageElement, config: GoonDeckPostEffectsConfig) {
  const strength = Math.max(0, Math.min(2, config.artEmboss));
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  if (strength <= 0) return;

  const scratch = document.createElement('canvas');
  scratch.width = CANVAS_W;
  scratch.height = CANVAS_H;
  const sourceCtx = scratch.getContext('2d', { willReadFrequently: true });
  if (!sourceCtx) return;
  drawCoveredImage(sourceCtx, image, CANVAS_W, CANVAS_H);

  const source = sourceCtx.getImageData(0, 0, CANVAS_W, CANVAS_H);
  const lum = new Float32Array(CANVAS_W * CANVAS_H);
  for (let i = 0, p = 0; i < source.data.length; i += 4, p += 1) {
    lum[p] = (source.data[i] * 0.299 + source.data[i + 1] * 0.587 + source.data[i + 2] * 0.114) / 255;
  }

  const selected = hexToRgb(config.artEmbossColor || '#ffffff');
  const tolerance = Math.max(0, Math.min(1, config.artEmbossColorTolerance));
  const threshold = Math.max(0, Math.min(1, config.artEmbossThreshold));
  const minLuma = Math.min(config.artEmbossLumaMin, config.artEmbossLumaMax);
  const maxLuma = Math.max(config.artEmbossLumaMin, config.artEmbossLumaMax);
  const mask = new Float32Array(CANVAS_W * CANVAS_H);

  for (let y = 1; y < CANVAS_H - 1; y += 1) {
    for (let x = 1; x < CANVAS_W - 1; x += 1) {
      const p = y * CANVAS_W + x;
      const i = p * 4;
      let value = 0;

      if (config.artEmbossMode === 'edges') {
        const gx =
          -lum[p - CANVAS_W - 1] - lum[p - 1] * 2 - lum[p + CANVAS_W - 1] +
          lum[p - CANVAS_W + 1] + lum[p + 1] * 2 + lum[p + CANVAS_W + 1];
        const gy =
          -lum[p - CANVAS_W - 1] - lum[p - CANVAS_W] * 2 - lum[p - CANVAS_W + 1] +
          lum[p + CANVAS_W - 1] + lum[p + CANVAS_W] * 2 + lum[p + CANVAS_W + 1];
        value = smoothstep(threshold * 0.72, threshold * 0.72 + 0.24, Math.hypot(gx, gy));
      } else if (config.artEmbossMode === 'dark') {
        value = 1 - smoothstep(threshold, threshold + 0.36, lum[p]);
      } else if (config.artEmbossMode === 'bright') {
        value = smoothstep(1 - threshold - 0.36, 1 - threshold, lum[p]);
      } else if (config.artEmbossMode === 'luma') {
        const lower = smoothstep(minLuma - 0.08, minLuma + 0.08, lum[p]);
        const upper = 1 - smoothstep(maxLuma - 0.08, maxLuma + 0.08, lum[p]);
        value = lower * upper;
      } else {
        const dr = source.data[i] - selected[0];
        const dg = source.data[i + 1] - selected[1];
        const db = source.data[i + 2] - selected[2];
        const distance = Math.hypot(dr, dg, db) / 441.7;
        value = 1 - smoothstep(tolerance * 0.65, Math.max(tolerance, tolerance * 0.65 + 0.04), distance);
      }

      mask[p] = Math.max(0, Math.min(1, value));
    }
  }

  const output = ctx.createImageData(CANVAS_W, CANVAS_H);
  const angle = (Math.max(0, Math.min(360, config.artEmbossDirection)) * Math.PI) / 180;
  const offset = Math.max(1, Math.round(1 + strength * 1.3));
  const dx = Math.round(Math.cos(angle) * offset);
  const dy = Math.round(Math.sin(angle) * offset);
  const alphaBase = 118 * strength;

  for (let y = 1; y < CANVAS_H - 1; y += 1) {
    for (let x = 1; x < CANVAS_W - 1; x += 1) {
      const p = y * CANVAS_W + x;
      const value = mask[p];
      if (value <= 0.01) continue;

      const highlightX = x - dx;
      const highlightY = y - dy;
      const shadowX = x + dx;
      const shadowY = y + dy;
      const alpha = Math.min(210, value * alphaBase);

      if (highlightX > 0 && highlightX < CANVAS_W && highlightY > 0 && highlightY < CANVAS_H) {
        const hi = (highlightY * CANVAS_W + highlightX) * 4;
        output.data[hi] = 255;
        output.data[hi + 1] = 255;
        output.data[hi + 2] = 255;
        output.data[hi + 3] = Math.max(output.data[hi + 3], alpha);
      }
      if (shadowX > 0 && shadowX < CANVAS_W && shadowY > 0 && shadowY < CANVAS_H) {
        const si = (shadowY * CANVAS_W + shadowX) * 4;
        output.data[si] = 18;
        output.data[si + 1] = 16;
        output.data[si + 2] = 24;
        output.data[si + 3] = Math.max(output.data[si + 3], alpha * 0.8);
      }
    }
  }

  ctx.putImageData(output, 0, 0);
}

function pseudoRandom01(seed: number, salt: number): number {
  const v = Math.sin(seed * 12.9898 + salt * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

export function ArtEmbossCanvas({
  artUrl,
  config,
  glareX = 50,
  glareY = 50,
  maskSize = 1,
}: {
  artUrl: string;
  config: GoonDeckPostEffectsConfig;
  glareX?: number;
  glareY?: number;
  maskSize?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || config.artEmboss <= 0) {
      ctx?.clearRect(0, 0, CANVAS_W, CANVAS_H);
      return;
    }

    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.src = artUrl;
    image.onload = () => drawEmboss(ctx, image, config);
  }, [artUrl, config]);

  if (config.artEmboss <= 0) return null;

  const canvas = (
    <canvas
      ref={canvasRef}
      width={CANVAS_W}
      height={CANVAS_H}
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{ mixBlendMode: 'overlay' }}
      aria-hidden="true"
    />
  );

  if (config.artEmbossMask.shape === 'none') return canvas;

  const shineAngle = 110 + (glareX - 50) * 0.28;
  let posX = 100 - glareX;
  let posY = 100 - glareY;
  let angle = shineAngle;
  if (config.artEmbossMask.motion === 'reverseMirror') {
    posX = glareX;
    posY = glareY;
  } else if (config.artEmbossMask.motion === 'random') {
    const seed = glareX * 0.017 + glareY * 0.031;
    posX = pseudoRandom01(seed, 11.3) * 100;
    posY = pseudoRandom01(seed, 17.9) * 100;
    angle = pseudoRandom01(seed, 23.7) * 360;
  }

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" style={maskShapeStyle(config.artEmbossMask.shape, posX, posY, angle, maskSize)}>
      {canvas}
    </div>
  );
}
